import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
import { formatApiError } from '../lib/apiError';
import { WorkerFormDialog } from '../components/workers/WorkerFormDialog';
import { WorkerCardList } from '../components/workers/WorkerCardList';
import { WorkerTable } from '../components/workers/WorkerTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyWorker = {
  name: '',
  phone: '',
  address: '',
  id_proof: '',
  emergency_contact: '',
  daily_wage: ''
};

const Workers = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [formData, setFormData] = useState(emptyWorker);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { format: fmt, symbol } = useCurrency();

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workers`, {
        params: showArchived ? { include_archived: true } : {}
      });
      setWorkers(response.data);
    } catch (error) {
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.daily_wage) {
      toast.error('Name, Phone, and Daily Wage are required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { ...formData, daily_wage: parseFloat(formData.daily_wage) };
      if (editingWorker) {
        await axios.put(`${API_URL}/api/workers/${editingWorker.id}`, payload);
        toast.success('Worker updated successfully');
      } else {
        await axios.post(`${API_URL}/api/workers`, payload);
        toast.success('Worker added successfully');
      }
      setIsDialogOpen(false);
      setEditingWorker(null);
      setFormData(emptyWorker);
      fetchWorkers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to save worker'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (worker) => {
    setEditingWorker(worker);
    setFormData({
      name: worker.name,
      phone: worker.phone,
      address: worker.address,
      id_proof: worker.id_proof,
      emergency_contact: worker.emergency_contact,
      daily_wage: worker.daily_wage.toString()
    });
    setIsDialogOpen(true);
  };

  const handleArchive = async (workerId) => {
    if (!window.confirm(
      'Archive this worker?\n\nThey will be hidden from your worker list and attendance, but all their wage history stays in Reports. You can restore them any time.'
    )) return;

    try {
      await axios.delete(`${API_URL}/api/workers/${workerId}`);
      toast.success('Worker archived — their history has been kept');
      fetchWorkers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to archive worker'));
    }
  };

  const handleRestore = async (workerId) => {
    try {
      await axios.post(`${API_URL}/api/workers/${workerId}/restore`);
      toast.success('Worker restored');
      fetchWorkers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to restore worker'));
    }
  };

  const openAddDialog = () => {
    setEditingWorker(null);
    setFormData(emptyWorker);
    setIsDialogOpen(true);
  };

  const filteredWorkers = workers.filter(worker =>
    worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="workers-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Workers
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            {workers.length} total workers
          </p>
        </div>

        <WorkerFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onAddClick={openAddDialog}
          editingWorker={editingWorker}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          symbol={symbol}
        />
      </div>

      <Card className="bg-white border border-[#D1D1CD] rounded-lg shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C5C5C]" />
            <Input
              data-testid="worker-search-input"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 border-[#D1D1CD]"
            />
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium text-[#5C5C5C]">Archived workers:</Label>
            <Button
              variant={showArchived ? 'default' : 'outline'}
              size="sm"
              data-testid="toggle-archived-btn"
              onClick={() => setShowArchived(!showArchived)}
              className={showArchived ? 'bg-[#2E4632] hover:bg-[#1F3122]' : 'border-[#D1D1CD]'}
            >
              {showArchived ? 'Showing archived' : 'Hidden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <WorkerCardList
        workers={filteredWorkers}
        searchTerm={searchTerm}
        fmt={fmt}
        onEdit={handleEdit}
        onArchive={handleArchive}
        onRestore={handleRestore}
      />

      <WorkerTable
        workers={filteredWorkers}
        searchTerm={searchTerm}
        fmt={fmt}
        onEdit={handleEdit}
        onArchive={handleArchive}
        onRestore={handleRestore}
      />
    </div>
  );
};

export default Workers;
